import { useState } from 'react';
import { useAppDispatch } from '../../../Redux/hooks';
import { upsert } from '../../../Redux/studentsSlice';
import { useTranslation } from '../../../i18n/useTranslation';
import type { StudentAbsenceModel, StudentModel } from '../../../Models/StudentModel';
import { formatDate } from '../../../Utils/DateUtil';
import { StudentService } from '../../../Services/StudentService';

/**
 * Known-in-advance gaps: a vacation, a broken arm, anything that takes a
 * student off the calendar between two dates. The recurring slot itself is
 * untouched, so the week they are back needs nothing re-entered; the
 * calendar's own planned-lesson suggestions simply stop for those dates
 * (see Calendar.tsx's `isAbsent`).
 *
 * Saved on the spot, the same as the recurring hours beside it: absences have
 * their own endpoints, and holding them for the form's submit would mean
 * inventing a diff for a list the server already owns.
 */
export function AbsenceEditor({ student }: { student: StudentModel }) {
  const { tr, language } = useTranslation();
  const dispatch = useAppDispatch();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [note, setNote] = useState('');
  const [noteEn, setNoteEn] = useState('');
  const [busy, setBusy] = useState(false);

  function replaceAbsences(absences: StudentAbsenceModel[]) {
    dispatch(upsert({ ...student, absences }));
  }

  async function add() {
    if (!startDate || !endDate || endDate < startDate) return;
    setBusy(true);
    try {
      const absence = await StudentService.addAbsence(student.name, {
        startDate,
        endDate,
        note: note.trim() || null,
        noteEn: noteEn.trim() || null
      });
      replaceAbsences([...student.absences, absence]);
      setStartDate('');
      setEndDate('');
      setNote('');
      setNoteEn('');
    } catch {
      // The list on screen is still what the server has; nothing was added.
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    setBusy(true);
    try {
      await StudentService.removeAbsence(id);
      replaceAbsences(student.absences.filter(absence => absence.id !== id));
    } catch {
      // Same: the absence stays listed because it is still stored.
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="editor-section is-wide">
      <h3>{tr('absences')}</h3>

      {student.absences.length > 0 && (
        <ul className="editor-chip-list">
          {student.absences.map(absence => (
            <li key={absence.id}>
              <bdi>
                {formatDate(absence.startDate, language)} – {formatDate(absence.endDate, language)}
                {absence.note ? ` · ${language === 'en' ? absence.noteEn?.trim() || absence.note : absence.note}` : ''}
              </bdi>
              <button type="button" onClick={() => void remove(absence.id)} disabled={busy} aria-label={tr('remove')}>×</button>
            </li>
          ))}
        </ul>
      )}

      <div className="editor-row">
        <label className="field">
          <span>{tr('absenceStart')}</span>
          <input className="text-input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </label>
        <label className="field">
          <span>{tr('absenceEnd')}</span>
          <input className="text-input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </label>
        <label className="field">
          <span>{tr('absenceNote')}</span>
          <input className="text-input" value={note} onChange={e => setNote(e.target.value)} placeholder={tr('absenceNotePlaceholder')} />
        </label>
        <label className="field">
          <span>{tr('absenceNoteEn')}</span>
          <input className="text-input" dir="ltr" value={noteEn} onChange={e => setNoteEn(e.target.value)} />
        </label>
        {/* type=button, or it submits the form it is nested in. */}
        <button
          type="button"
          className="ghost-button"
          onClick={() => void add()}
          disabled={busy || !startDate || !endDate || endDate < startDate}
        >
          {tr('add')}
        </button>
      </div>
    </section>
  );
}
